<template lang="pug">
section(class="hero-section")
  div(class="hero-inner")
    div(class="hero-copy")
      p(class="hero-eyebrow")
        span(class="eyebrow-line" aria-hidden="true")
        | PANDAFAN / DEDICATED
      h1(class="hero-title") {{ $t('dedicated_servers.title') }}
      p(class="hero-subtitle") {{ $t('dedicated_servers.subtitle') }}
      div(class="hero-actions")
        a(href="#dedicated-catalog" class="hero-primary" @click.prevent="scrollToSection('dedicated-catalog')")
          span {{ $t('dedicated_servers.hero.view_plans') }}
          ArrowDown(aria-hidden="true")
        a(href="#dedicated-compare" class="hero-secondary" @click.prevent="scrollToSection('dedicated-compare')")
          span {{ $t('dedicated_servers.hero.compare_lines') }}
          ArrowRight(aria-hidden="true")
    div(class="hero-network" aria-hidden="true")
      svg(class="network-routes" viewBox="0 0 420 220" fill="none")
        path(class="network-track" d="M30 110H140Q160 110 160 90V50Q160 30 180 30H370M30 110H370M30 110H140Q160 110 160 130V170Q160 190 180 190H370")
        path(class="network-signal" d="M30 110H140Q160 110 160 90V50Q160 30 180 30H370M30 110H370M30 110H140Q160 110 160 130V170Q160 190 180 190H370")
        circle(cx="30" cy="110" r="7" class="network-node")
        circle(cx="370" cy="30" r="5" class="network-node")
        circle(cx="370" cy="110" r="5" class="network-node")
        circle(cx="370" cy="190" r="5" class="network-node")
      div(class="network-server")
        Server(class="network-server-icon")
        span PANDAFAN
        div(class="server-slots")
          i(v-for="n in 3" :key="n")

div(class="page-container min-h-screen")
  div(ref="pageRoot" class="dedicated-main")
    Alert(v-if="regionsError" variant="destructive" class="mb-6")
      AlertTitle {{ $t('error.str') }}
      AlertDescription {{ regionsError.message }}

    Tabs(id="dedicated-catalog" v-model="activeDedicatedTab" default-value="global" orientation="horizontal" class="dedicated-catalog flex w-full flex-col")
      TabsList(class="dedicated-tabs")
        TabsTrigger(value="global" class="dedicated-tab" :aria-label="$t('dedicated_servers.locations.global') + ' · ' + $t('dedicated_servers.public_line')")
          Globe2(class="dedicated-tab-icon" aria-hidden="true")
          span(class="dedicated-tab-label")
            span {{ $t('dedicated_servers.locations.global') }}
            span(class="dedicated-tab-network-type") {{ $t('dedicated_servers.public_line') }}
        TabsTrigger(value="taiwan" class="dedicated-tab" :aria-label="$t('dedicated_servers.locations.taiwan') + ' · ' + $t('dedicated_servers.public_line')")
          House(class="dedicated-tab-icon" aria-hidden="true")
          span(class="dedicated-tab-label")
            span {{ $t('dedicated_servers.locations.taiwan') }}
            span(class="dedicated-tab-network-type") {{ $t('dedicated_servers.public_line') }}
        TabsTrigger(value="japan-iplc" class="dedicated-tab")
          Waypoints(class="dedicated-tab-icon" aria-hidden="true")
          span(class="sm:hidden") {{ $t('dedicated_servers.locations.japan_iplc_short') }}
          span(class="hidden sm:inline") {{ $t('dedicated_servers.locations.japan_iplc') }}
      TabsContent(value="taiwan" class="mt-0")
        div(class="taiwan-panel")
          div(class="option-header")
            Badge(class="unlimited-badge")
              Icon(name="mdi:infinity" class="mr-1")
              | {{ $t('dedicated_servers.unlimited_transfer') }}
            p(class="taiwan-description") {{ $t('dedicated_servers.taiwan_description') }}
            p(class="taiwan-selling-point")
              Icon(name="mdi:home-city-outline")
              span {{ $t('dedicated_servers.taiwan_selling_point') }}
            p(class="bandwidth-note") {{ $t('dedicated_servers.public_bandwidth_note') }}
          div(class="pricing-grid grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3")
            Card(v-for="plan in taiwanPlans" :key="plan.id" class="pricing-card" :class="{ 'is-popular': plan.popular }")
              CardContent(class="pricing-card-content")
                Badge(v-if="plan.popular" class="popular-badge") {{ $t('popular') }}
                h3(class="pricing-plan-name") {{ plan.name }}
                div(class="pricing-price-row")
                  span(class="pricing-price") €{{ plan.price }}
                  span(class="pricing-period") {{ $t('dedicated_servers.per_month') }}
                p(class="setup-fee-line")
                  span(v-if="plan.setupFee") + €100 {{ $t('dedicated_servers.setup_fee') }}
                div(class="pricing-specs")
                  div(v-for="spec in plan.specs" :key="spec.icon" class="flex items-center gap-3 text-sm")
                    Icon(:name="spec.icon" class="h-4 w-4 text-primary")
                    span {{ spec.value }}
                Button(type="button" class="pricing-action mt-auto w-full" :variant="plan.popular ? 'default' : 'outline'" @click="contactStaticPlan(plan.id)")
                  | {{ $t('dedicated_servers.contact_sales') }}

      TabsContent(value="japan-iplc" class="mt-0")
        div(class="custom-line-shell")
          div(class="custom-line-hero")
            div(class="custom-line-intro")
              div(class="custom-line-badge-row")
                Badge(variant="outline" class="rounded-lg px-4 py-1")
                  Icon(name="mdi:server-network" class="mr-1")
                  | {{ $t('dedicated_servers.custom_private_line.dedicated_machine') }}
                Badge(class="unlimited-badge")
                  Icon(name="mdi:infinity" class="mr-1")
                  | {{ $t('dedicated_servers.custom_private_line.unlimited_traffic') }}
              p(class="custom-line-description") {{ $t('dedicated_servers.custom_private_line.description') }}
              div(class="custom-line-highlights")
                div(v-for="highlight in customLineHighlights" :key="highlight.title" class="custom-line-highlight")
                  Icon(:name="highlight.icon" class="text-primary")
                  div
                    strong {{ $t(highlight.title) }}
                    span {{ $t(highlight.description) }}
            div(class="custom-line-network-panel")
              ol(class="private-route")
                li(class="private-route-terminal") {{ customLineNetworkLabels.you }}
                li(class="private-route-stop")
                  span {{ customLineNetworkLabels.edgePrefix }}
                  strong(:key="selectedCustomLineEntry" class="route-value") {{ customLineNetworkLabels.edge }}
                li(class="private-route-stop")
                  span {{ $t('dedicated_servers.custom_private_line.exit') }}
                  strong(:key="selectedCustomLineExit" class="route-value") {{ customLineNetworkLabels.corePrefix }} {{ customLineNetworkLabels.core }}
                li(class="private-route-terminal") {{ customLineNetworkLabels.exit }}
          p(class="bandwidth-note") {{ $t('dedicated_servers.custom_private_line.bandwidth_note') }}
          div(class="custom-line-config-grid")
            div(class="custom-line-controls")
              div(class="custom-line-field")
                p {{ $t('dedicated_servers.custom_private_line.entry_hint') }}
                div(class="custom-line-option-row custom-line-option-row--entry")
                  Button(
                    v-for="entry in customLineEntrances"
                    :key="entry.value"
                    type="button"
                    class="custom-line-choice"
                    :class="{ 'is-selected': selectedCustomLineEntry === entry.value }"
                    :aria-pressed="selectedCustomLineEntry === entry.value"
                    :variant="selectedCustomLineEntry === entry.value ? 'default' : 'outline'"
                    @click="selectedCustomLineEntry = entry.value"
                  )
                    | {{ $t(entry.label) }}
              div(class="custom-line-field")
                p {{ $t('dedicated_servers.custom_private_line.exit_hint') }}
                div(class="custom-line-option-row custom-line-option-row--exit")
                  Button(
                    v-for="exit in customLineExits"
                    :key="exit.value"
                    type="button"
                    class="custom-line-choice"
                    :class="{ 'is-selected': selectedCustomLineExit === exit.value }"
                    :aria-pressed="selectedCustomLineExit === exit.value"
                    :variant="selectedCustomLineExit === exit.value ? 'default' : 'outline'"
                    @click="selectedCustomLineExit = exit.value"
                  )
                    span(class="mr-2") {{ exit.flag }}
                    | {{ $t(exit.label) }}
              div(class="custom-line-field")
                div(class="custom-line-bandwidth-header")
                  Label {{ $t('dedicated_servers.custom_private_line.bandwidth') }}
                  div(class="custom-line-bandwidth-value")
                    input(
                      v-model.number="customLineBandwidth"
                      type="number"
                      min="100"
                      max="1000"
                      step="10"
                      class="bandwidth-input"
                      :aria-label="$t('dedicated_servers.custom_private_line.bandwidth')"
                    )
                    span {{ $t('dedicated_servers.custom_private_line.bandwidth_unit') }}
                Slider(v-model="customLineBandwidthSlider" :min="100" :max="1000" :step="10" class="mt-5")
            div(class="custom-line-quote")
              div(class="custom-line-quote-label") {{ $t('dedicated_servers.custom_private_line.monthly_quote') }}
              div(class="custom-line-quote-price") {{ formatEuro(customLineMonthlyPrice) }}
              div(class="custom-line-quote-copy") {{ $t('dedicated_servers.custom_private_line.billing', { rate: CUSTOM_LINE_EUR_PER_MBPS, bandwidth: customLineBandwidth }) }}
              Button(type="button" class="custom-line-cta" @click="contactCustomPrivateLine")
                Icon(name="mdi:message-text-outline" class="mr-2")
                | {{ $t('dedicated_servers.custom_private_line.contact_button') }}

      TabsContent(value="global" class="mt-0")
        dl(class="global-stats")
          div(class="global-stat")
            dt {{ $t('dedicated_servers.browser.stats.uplink') }}
            dd
              PandaCoreCountUp(:value="40")
              span(class="global-stat-unit") Gbps
          div(class="global-stat")
            dt {{ $t('dedicated_servers.browser.stats.provisioning') }}
            dd
              PandaCoreCountUp(:value="15")
              span(class="global-stat-unit") min
          div(v-if="regions.length" class="global-stat")
            dt {{ $t('dedicated_servers.browser.stats.locations') }}
            dd
              PandaCoreCountUp(:value="regions.length")
        div(class="global-browser-grid")
          Card(class="catalog-card region-catalog")
            CardHeader(class="space-y-4 p-5")
              div(class="flex items-baseline justify-between gap-3")
                CardTitle(class="text-lg font-semibold tracking-tight") {{ $t('dedicated_servers.browser.locations') }}
                CardDescription(class="shrink-0 text-sm")
                  span(v-if="regionsPending") {{ $t('dedicated_servers.browser.loading_regions') }}
                  span(v-else) {{ $t('dedicated_servers.browser.regions_count', { count: regions.length }) }}
              div(class="region-search")
                Search(aria-hidden="true")
                Input(v-model="searchQuery" class="h-10 rounded-lg" :placeholder="$t('dedicated_servers.browser.search_locations')" :aria-label="$t('dedicated_servers.browser.search_locations')" autocomplete="off")
              div(class="flex items-center gap-2")
                Button(type="button" variant="outline" class="latency-action h-10 flex-1 rounded-lg transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.98]" size="sm" :disabled="latencyRunning || !latencyCandidates.length" @click="runLatencyTest")
                  Icon(v-if="latencyRunning" name="mdi:loading" class="mr-2 animate-spin")
                  Icon(v-else name="mdi:speedometer" class="mr-2")
                  span(v-if="latencyRunning") {{ $t('dedicated_servers.browser.testing_latency') }}
                  span(v-else) {{ $t('dedicated_servers.browser.latency_test') }}
                Button(v-if="latencySortActive" type="button" class="h-10 rounded-lg transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.96]" size="icon" variant="outline" :title="$t('dedicated_servers.browser.reset_sort')" @click="latencySortActive = false")
                  Icon(name="mdi:sort-variant-off")
              button(v-if="latencyRecommendation" type="button" class="recommended-location-card w-full rounded-xl border border-primary/30 bg-primary/5 p-3 text-left text-sm transition-[background-color,transform,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-primary/10 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50" @click="selectRecommendedRegion")
                div(class="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground") {{ $t('dedicated_servers.browser.recommended_location') }}
                div(class="mt-1 font-semibold") {{ latencyRecommendation.locationName }}
                div(class="mt-3 grid gap-2 text-xs")
                  div(class="flex items-center justify-between gap-3")
                    span(class="text-muted-foreground") {{ $t('dedicated_servers.browser.recommended_network') }}
                    span(class="font-medium") {{ latencyRecommendation.networkType }}
                  div(class="flex items-center justify-between gap-3")
                    span(class="text-muted-foreground") {{ $t('dedicated_servers.browser.demo_ip') }}
                    span(class="font-mono") {{ latencyRecommendation.demoIP }}
                  div(class="flex items-center justify-between gap-3")
                    span(class="text-muted-foreground") {{ $t('dedicated_servers.browser.recommended_latency') }}
                    span(class="font-medium" :class="latencyTextClass({ status: 'done', bestMs: latencyRecommendation.bestMs }, latencyRecommendationRegion)") {{ formatMs(latencyRecommendation.bestMs) }}
            CardContent(class="p-0")
              div(class="misaka-region-list")
                button(
                    v-for="region in displayedRegions"
                    :key="region.id"
                    type="button"
                    class="relative w-full px-5 py-4 text-left transition-[background-color,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-muted/50 active:scale-[0.99]"
                    :class="{ 'bg-primary/10': selectedRegionId === region.id }"
                    :aria-pressed="selectedRegionId === region.id"
                    @click="selectRegion(region)"
                  )
                    div(class="flex items-start justify-between gap-3")
                      div(class="min-w-0")
                        div(class="flex items-center gap-2")
                          Badge(v-if="region.network_type" :class="networkTypeBadgeClass(region.network_type)" :title="networkTypeDescription(region.network_type)") {{ region.network_type }}
                          span(class="truncate text-sm font-semibold") {{ displayRegionName(region) }}
                      div(v-if="regionLatency[region.id]" class="shrink-0 text-right text-xs")
                        div(class="flex h-5 items-center justify-end")
                          Icon(v-if="isLatencyPending(regionLatency[region.id])" name="mdi:loading" class="h-4 w-4 animate-spin text-muted-foreground")
                          span(v-else class="font-mono tabular-nums" :class="latencyTextClass(regionLatency[region.id], region)") {{ latencyLabel(region) }}
                        div(class="text-muted-foreground") {{ isLatencyPending(regionLatency[region.id]) ? '' : latencyQualityLabel(regionLatency[region.id], region) }}
                div(v-if="!displayedRegions.length" class="p-6 text-sm text-muted-foreground")
                  | {{ $t('dedicated_servers.browser.no_locations') }}

          div(class="plan-column")
            Card(class="catalog-card plan-catalog")
              CardHeader(class="border-b p-5")
                div(class="plan-heading")
                  div
                    CardTitle(:key="selectedRegionId" class="region-title text-2xl font-semibold tracking-tight") {{ selectedRegion ? displayRegionName(selectedRegion) : $t('dedicated_servers.browser.plans') }}
                    div(v-if="selectedRegion?.network_type" class="mt-2 text-sm text-muted-foreground")
                      Badge(:class="networkTypeBadgeClass(selectedRegion.network_type)" :title="networkTypeDescription(selectedRegion.network_type)") {{ selectedRegion.network_type }}
                      span(class="ml-2 text-xs text-muted-foreground") {{ networkTypeDescription(selectedRegion.network_type) }}
                  div(class="flex items-center gap-2")
                    Button(type="button" class="rounded-lg transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.98]" :disabled="plansLoading || !selectedPlan" @click="contactSales()")
                      Icon(name="mdi:message-text-outline" class="mr-2")
                      | {{ selectedPlan ? $t('dedicated_servers.browser.consult_plan', { traffic: formatTransfer(selectedPlan.transfer) }) : $t('dedicated_servers.contact_sales') }}
              CardContent(class="p-0")
                Alert(v-if="plansError || stockError" variant="destructive" class="m-4 rounded-lg")
                  AlertTitle {{ $t('error.str') }}
                  AlertDescription {{ plansError || stockError }}
                div(class="plans-table-scroll")
                  Table(class="plans-table")
                    TableHeader
                      TableRow
                        TableHead {{ $t('dedicated_servers.browser.traffic') }}
                        TableHead(class="text-right") {{ $t('dedicated_servers.browser.price') }}
                        TableHead(class="text-right") {{ $t('dedicated_servers.browser.status') }}
                    TableBody
                      TableRow(v-if="plansLoading && !dedicatedPlans.length")
                        TableCell(colspan="3" class="py-8 text-center text-sm text-muted-foreground")
                          div(class="inline-flex items-center gap-2")
                            Icon(name="mdi:loading" class="animate-spin")
                            span {{ $t('dedicated_servers.browser.loading_plans') }}
                      TableRow(
                        v-for="plan in dedicatedPlans"
                        :key="plan.id"
                        class="transition-colors"
                        :class="[isPlanInStock(plan) ? 'cursor-pointer hover:bg-muted/40' : 'plan-row-unavailable', { 'bg-primary/10': String(plan.id) === selectedPlanId }]"
                        :aria-disabled="!isPlanInStock(plan)"
                        @click="selectPlan(plan)"
                      )
                        TableCell
                          label(class="plan-select")
                            input(type="radio" name="dedicated-plan" :value="String(plan.id)" :checked="String(plan.id) === selectedPlanId" :disabled="!isPlanInStock(plan)" @change="selectPlan(plan)" @click.stop)
                            span {{ formatTrafficPlan(plan) }}
                          div(class="text-xs text-muted-foreground") {{ formatTrafficBilling(plan) }}
                        TableCell(class="plan-price text-right tabular-nums") {{ formatEuro(plan.price_monthly || 0) }}{{ $t('dedicated_servers.per_month') }}
                        TableCell(class="text-right")
                          div(class="plan-status")
                            span(v-if="stockPending" class="plan-stock-checking" role="status")
                              span(class="sr-only") {{ $t('dedicated_servers.browser.checking_stock') }}
                            Badge(v-else :variant="planBadgeVariant(plan)" class="rounded-lg") {{ planStockLabel(plan) }}
                      TableRow(v-if="!plansLoading && !dedicatedPlans.length")
                        TableCell(colspan="3" class="py-8 text-center text-sm text-muted-foreground")
                          | {{ $t('dedicated_servers.browser.no_plans') }}
        p(class="bandwidth-note catalog-footnote")
          Info(aria-hidden="true")
          span {{ $t('dedicated_servers.public_bandwidth_note') }}

    DedicatedBandwidthDemo(id="dedicated-compare" class="reveal")

    div(class="dedicated-info-strip reveal")
      article(v-for="(info, index) in infoItems" :key="info.title" class="dedicated-info-item")
        span(class="info-index") {{ String(index + 1).padStart(2, '0') }}
        h3 {{ $t(info.title) }}
        p {{ $t(info.description) }}
        button(v-if="info.action" type="button" class="info-action" @click="contactStaticPlan('custom-global')")
          span {{ $t(info.action) }}
          ArrowRight(aria-hidden="true")

    section(class="dedicated-section dedicated-features reveal")
      div(class="section-heading")
        p(class="section-eyebrow")
          span(class="eyebrow-dot" aria-hidden="true")
          | PandaFan / {{ $t('dedicated_servers.title') }}
        h2 {{ $t('dedicated_servers.features.title') }}
      div(class="feature-grid")
        article(v-for="feature in featureItems" :key="feature.title" class="feature-item")
          component(:is="feature.icon" class="feature-icon" aria-hidden="true")
          h3 {{ $t(feature.title) }}
          p {{ $t(feature.description) }}

    section(class="dedicated-section dedicated-faq-section reveal")
      div(class="section-heading")
        p(class="section-eyebrow")
          span(class="eyebrow-dot" aria-hidden="true")
          | {{ $t('dedicated_servers.faq.eyebrow') }}
        h2 {{ $t('dedicated_servers.faq.title') }}
      Accordion(type="single" collapsible class="dedicated-faq-list")
        AccordionItem(v-for="item in faqItems" :key="item.value" :value="item.value" class="dedicated-faq-item")
          AccordionTrigger(class="dedicated-faq-trigger")
            span {{ $t(item.question) }}
            template(#icon)
              span(class="faq-plus" aria-hidden="true") +
          AccordionContent(class="dedicated-faq-answer")
            | {{ $t(item.answer) }}
</template>

<script lang="ts" setup>
import { ArrowDown, ArrowRight, Globe2, Headphones, House, Info, Monitor, Search, Server, Waypoints } from 'lucide-vue-next'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '~/utils/sonner'

interface MisakaSpeedtestRaw {
  url?: string
  label?: string
  name?: string
}

interface MisakaRegion {
  id: string
  region_id?: string
  slug: string
  name: string
  facility?: string
  description?: string
  country: string
  country_code?: string
  subdivision_code?: string
  contient?: string
  continent?: string
  available: boolean
  unavailable_reason?: string
  network_type?: string
  speedtests?: Array<string | MisakaSpeedtestRaw>
}

interface MisakaPlan {
  id: number | string
  transfer: number
  traffic_billing_model?: string
  price_monthly?: number
  available: boolean
}

type PlanStock = 'in_stock' | 'sold_out' | 'unknown'

interface MisakaStockResponse {
  checkedAt: string
  plans: Record<string, PlanStock>
}

interface MisakaRegionsResponse {
  fetchedAt: string
  regions: MisakaRegion[]
}

interface MisakaPlansResponse {
  fetchedAt: string
  region: string
  network_type?: string
  plans: MisakaPlan[]
}

interface SpeedtestTarget {
  baseURL: string
  label: string
  displayAddress: string
}

interface LatencyStats {
  minMs: number
  meanMs: number
  maxMs: number
  lastMs: number
  completed: number
  total: number
}

interface LatencyRecord {
  status: 'queued' | 'checking' | 'running' | 'done' | 'failed'
  bestMs?: number
  meanMs?: number
  maxMs?: number
  lastMs?: number
  tcpRttMs?: number
  completed?: number
  total?: number
  message?: string
}

interface LatencyRecommendation {
  regionId: string
  locationName: string
  networkType: string
  demoIP: string
  bestMs: number
}

const { t: $t, locale } = useI18n()
const session = useVisitorSession()
const user = useUser()
const activeDedicatedTab = ref('global')

const ZH_REGION_NAMES: Record<string, string> = {
  'Amsterdam': '阿姆斯特丹',
  'Ashburn': '阿什本',
  'Athens': '雅典',
  'Berlin': '柏林',
  'Bordeaux': '波尔多',
  'Brussels': '布鲁塞尔',
  'Bucharest': '布加勒斯特',
  'Copenhagen': '哥本哈根',
  'Dublin': '都柏林',
  'Hong Kong': '香港',
  'Istanbul': '伊斯坦布尔',
  'Johannesburg': '约翰内斯堡',
  'Kansas': '堪萨斯城',
  'Khabarovsk': '哈巴罗夫斯克',
  'Kyiv': '基辅',
  'Lagos': '拉各斯',
  'Lisbon': '里斯本',
  'London': '伦敦',
  'Los Angeles': '洛杉矶',
  'Madrid': '马德里',
  'Marseille': '马赛',
  'Miami': '迈阿密',
  'Milan': '米兰',
  'Montréal': '蒙特利尔',
  'Moscow': '莫斯科',
  'Oslo': '奥斯陆',
  'Prague': '布拉格',
  'San Jose': '圣何塞',
  'São Paulo': '圣保罗',
  'Seattle': '西雅图',
  'Secaucus': '锡考克斯',
  'Singapore': '新加坡',
  'St. Petersburg': '圣彼得堡',
  'Stockholm': '斯德哥尔摩',
  'Taipei': '台北',
  'Tokyo': '东京',
  'Vienna': '维也纳',
  'Warsaw': '华沙',
  'Zagreb': '萨格勒布',
  'Zurich': '苏黎世'
}

const ZH_SUBDIVISION_NAMES: Record<string, string> = {
  'US-CA': '加州',
  'US-FL': '佛罗里达州',
  'US-MO': '密苏里州',
  'US-NJ': '新泽西州',
  'US-VA': '弗吉尼亚州',
  'US-WA': '华盛顿州'
}

const ZH_COUNTRY_DISPLAY_NAMES = new Intl.DisplayNames(['zh-CN'], { type: 'region' })

const featureItems = [
  {
    icon: Monitor,
    title: 'dedicated_servers.features.client_integration',
    description: 'dedicated_servers.features.client_integration_desc'
  },
  {
    icon: Server,
    title: 'dedicated_servers.features.high_performance',
    description: 'dedicated_servers.features.high_performance_desc'
  },
  {
    icon: Waypoints,
    title: 'dedicated_servers.features.network_quality',
    description: 'dedicated_servers.features.network_quality_desc'
  },
  {
    icon: Headphones,
    title: 'dedicated_servers.features.support',
    description: 'dedicated_servers.features.support_desc'
  }
]

const infoItems = [
  {
    title: 'dedicated_servers.discount.title',
    description: 'dedicated_servers.discount.description'
  },
  {
    title: 'dedicated_servers.setup_fee_info.title',
    description: 'dedicated_servers.setup_fee_info.description'
  },
  {
    title: 'dedicated_servers.custom_global.title',
    description: 'dedicated_servers.custom_global.description',
    action: 'dedicated_servers.custom_global.contact_button'
  }
]

const faqItems = [
  {
    value: 'setup-time',
    question: 'dedicated_servers.faq.setup_time',
    answer: 'dedicated_servers.faq.setup_time_answer'
  },
  {
    value: 'bandwidth-upgrade',
    question: 'dedicated_servers.faq.bandwidth_upgrade',
    answer: 'dedicated_servers.faq.bandwidth_upgrade_answer'
  },
  {
    value: 'contract-terms',
    question: 'dedicated_servers.faq.contract_terms',
    answer: 'dedicated_servers.faq.contract_terms_answer'
  },
  {
    value: 'setup-fee',
    question: 'dedicated_servers.faq.setup_fee_question',
    answer: 'dedicated_servers.faq.setup_fee_answer'
  }
]

const pageRoot = ref<HTMLElement>()
let revealObserver: IntersectionObserver | null = null

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  history.replaceState(history.state, '', `#${id}`)
}

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.remove('reveal-pending')
        revealObserver?.unobserve(entry.target)
      })
    },
    { threshold: 0.08 }
  )

  // Server-rendered content stays visible; only offscreen content waits for a reveal.
  pageRoot.value?.querySelectorAll<HTMLElement>('.reveal').forEach((element) => {
    if (element.getBoundingClientRect().top < window.innerHeight) return
    element.classList.add('reveal-pending')
    revealObserver?.observe(element)
  })
})

onBeforeUnmount(() => {
  revealObserver?.disconnect()
})

useHead({
  title: () => $t('dedicated_servers.page_title'),
  meta: [
    { name: 'description', content: () => $t('dedicated_servers.page_description') }
  ]
})

const taiwanPlans = computed(() => [
  {
    id: 'taiwan-professional',
    name: $t('dedicated_servers.plans.professional.name'),
    price: 350,
    setupFee: false,
    specs: [
      { icon: 'mdi:speedometer', value: '100 Mbps' },
      { icon: 'mdi:infinity', value: $t('dedicated_servers.unlimited_data') },
      { icon: 'mdi:ip-network', value: '1 IP' }
    ]
  },
  {
    id: 'taiwan-business',
    name: $t('dedicated_servers.plans.business.name'),
    price: 550,
    popular: true,
    setupFee: false,
    specs: [
      { icon: 'mdi:speedometer', value: '300 Mbps' },
      { icon: 'mdi:infinity', value: $t('dedicated_servers.unlimited_data') },
      { icon: 'mdi:ip-network', value: '3 IPs' }
    ]
  },
  {
    id: 'taiwan-enterprise',
    name: $t('dedicated_servers.plans.enterprise.name'),
    price: 750,
    setupFee: false,
    specs: [
      { icon: 'mdi:speedometer', value: '500 Mbps' },
      { icon: 'mdi:infinity', value: $t('dedicated_servers.unlimited_data') },
      { icon: 'mdi:ip-network', value: '6 IPs' }
    ]
  }
])

const customLineEntrances = [
  { value: 'shanghai', label: 'dedicated_servers.custom_private_line.entries.shanghai' },
  { value: 'guangzhou', label: 'dedicated_servers.custom_private_line.entries.guangzhou' }
]

const customLineExits = [
  { value: 'japan', label: 'dedicated_servers.custom_private_line.exits.japan', flag: '🇯🇵' },
  { value: 'us', label: 'dedicated_servers.custom_private_line.exits.us', flag: '🇺🇸' },
  { value: 'hongkong', label: 'dedicated_servers.custom_private_line.exits.hongkong', flag: '🇭🇰' }
]

const customLineHighlights = [
  {
    icon: 'mdi:transit-connection-variant',
    title: 'dedicated_servers.custom_private_line.highlights.bgp.title',
    description: 'dedicated_servers.custom_private_line.highlights.bgp.description'
  },
  {
    icon: 'mdi:shield-lock-outline',
    title: 'dedicated_servers.custom_private_line.highlights.private_network.title',
    description: 'dedicated_servers.custom_private_line.highlights.private_network.description'
  }
]

const selectedCustomLineEntry = ref(customLineEntrances[0].value)
const selectedCustomLineExit = ref(customLineExits[0].value)
const CUSTOM_LINE_EUR_PER_MBPS = 30
const customLineBandwidth = ref(100)
const customLineBandwidthSlider = computed({
  get: () => [customLineBandwidth.value],
  set(value: number[]) {
    customLineBandwidth.value = Number(value[0])
  }
})
const customLineMonthlyPrice = computed(() => customLineBandwidth.value * CUSTOM_LINE_EUR_PER_MBPS)
const selectedCustomLineEntryLabel = computed(() => $t(customLineEntrances.find(entry => entry.value === selectedCustomLineEntry.value)?.label || customLineEntrances[0].label))
const selectedCustomLineExitLabel = computed(() => $t(customLineExits.find(exit => exit.value === selectedCustomLineExit.value)?.label || customLineExits[0].label))
const selectedCustomLineExitFlag = computed(() => customLineExits.find(exit => exit.value === selectedCustomLineExit.value)?.flag || customLineExits[0].flag)
const customLineNetworkLabels = computed(() => ({
  edgePrefix: $t('dedicated_servers.custom_private_line.network_entry'),
  edge: selectedCustomLineEntryLabel.value,
  corePrefix: selectedCustomLineExitFlag.value,
  core: selectedCustomLineExitLabel.value,
  you: $t('dedicated_servers.custom_private_line.route_you'),
  exit: $t('dedicated_servers.custom_private_line.route_internet')
}))

watch(customLineBandwidth, (value) => {
  const normalized = Number(value)
  if (!Number.isFinite(normalized)) {
    customLineBandwidth.value = 100
    return
  }

  const clamped = Math.min(1000, Math.max(100, Math.round(normalized / 10) * 10))
  if (clamped !== normalized) {
    customLineBandwidth.value = clamped
  }
})

const api = useAPI()
const { data: regionsPayload, pending: regionsPending, error: regionsError } = await useAsyncData<MisakaRegionsResponse>('misaka-regions', () => api.get('misaka/regions') as Promise<MisakaRegionsResponse>)

const searchQuery = ref('')
const selectedRegionId = ref('')
const selectedPlanId = ref('')
const plans = ref<MisakaPlan[]>([])
const plansLoading = ref(false)
const plansError = ref('')
// Misaka lists plans it cannot sell; only plans the stock check confirms can be consulted on.
const planStock = ref<Record<string, PlanStock> | null>(null)
const stockError = ref('')
const latencyRunning = ref(false)
const latencySortActive = ref(false)
const regionLatency = reactive<Record<string, LatencyRecord>>({})
const endpointLatency = reactive<Record<string, LatencyRecord>>({})
const latencyRecommendation = ref<LatencyRecommendation | null>(null)

const regions = computed(() => {
  return (regionsPayload.value?.regions || [])
    .filter(region => region.available && !isHiddenRegion(region))
})
const selectedRegion = computed(() => regions.value.find(region => region.id === selectedRegionId.value))
const latencyRecommendationRegion = computed(() => {
  if (!latencyRecommendation.value) {
    return undefined
  }

  return regions.value.find(region => region.id === latencyRecommendation.value?.regionId)
})
const dedicatedPlans = computed(() => {
  return [...plans.value]
    .filter(plan => plan.available)
    .sort((left, right) => {
      const transferDelta = Number(left.transfer) - Number(right.transfer)
      if (transferDelta !== 0) {
        return transferDelta
      }

      return Number(left.price_monthly) - Number(right.price_monthly)
    })
})
const selectedPlan = computed(() => dedicatedPlans.value.find(plan => String(plan.id) === selectedPlanId.value && isPlanInStock(plan)))
const stockPending = computed(() => !planStock.value && !stockError.value)
const latencyCandidates = computed(() => filteredRegions.value.filter(region => speedtestsForRegion(region).length > 0))

const filteredRegions = computed(() => {
  return regions.value.filter(region => matchesRegionSearch(region, searchQuery.value))
})

const displayedRegions = computed(() => {
  const sorted = [...filteredRegions.value].sort(sortRegions)
  if (!latencySortActive.value) {
    return sorted
  }

  return sorted.sort((left, right) => {
    const leftLatency = regionLatency[left.id]?.bestMs ?? Number.POSITIVE_INFINITY
    const rightLatency = regionLatency[right.id]?.bestMs ?? Number.POSITIVE_INFINITY
    if (leftLatency !== rightLatency) {
      return leftLatency - rightLatency
    }
    return sortRegions(left, right)
  })
})

watch(regions, (nextRegions) => {
  if (!nextRegions.length) {
    return
  }

  const stillSelected = nextRegions.some(region => region.id === selectedRegionId.value)
  if (!stillSelected) {
    selectedRegionId.value = [...nextRegions].sort(sortRegions)[0].id
  }
}, { immediate: true })

watch(displayedRegions, (nextRegions) => {
  if (!nextRegions.length) {
    selectedRegionId.value = ''
    return
  }

  const stillVisible = nextRegions.some(region => region.id === selectedRegionId.value)
  if (!stillVisible) {
    selectedRegionId.value = nextRegions[0].id
  }
}, { immediate: true })

let plansRequestId = 0
watch(selectedRegionId, async (regionId) => {
  plansError.value = ''
  stockError.value = ''
  planStock.value = null
  selectedPlanId.value = ''

  if (!regionId) {
    plans.value = []
    selectedPlanId.value = ''
    return
  }

  const selected = regions.value.find(region => region.id === regionId)
  if (!selected) {
    plans.value = []
    selectedPlanId.value = ''
    return
  }

  const requestId = ++plansRequestId
  plansLoading.value = true
  try {
    const query: Record<string, string> = {}
    if (selected.network_type) {
      query.networkType = selected.network_type
    }
    const payload = await api.get(`misaka/regions/${selected.region_id || selected.id}/plans`, { params: query }) as MisakaPlansResponse
    if (requestId !== plansRequestId) {
      return
    }

    plans.value = payload.plans
    if (dedicatedPlans.value.length) {
      void loadPlanStock(selected, query, requestId)
    }
  } catch (error) {
    if (requestId !== plansRequestId) {
      return
    }
    plans.value = []
    selectedPlanId.value = ''
    plansError.value = error instanceof Error ? error.message : $t('dedicated_servers.browser.plans_error')
  } finally {
    if (requestId === plansRequestId) {
      plansLoading.value = false
    }
  }
}, { immediate: true })

async function loadPlanStock(region: MisakaRegion, query: Record<string, string>, requestId: number) {
  try {
    const payload = await api.get(`misaka/regions/${region.region_id || region.id}/stock`, { params: query }) as MisakaStockResponse
    if (requestId !== plansRequestId) {
      return
    }

    planStock.value = payload.plans
    selectedPlanId.value = String(dedicatedPlans.value.find(isPlanInStock)?.id || '')
  } catch (error) {
    if (requestId !== plansRequestId) {
      return
    }
    console.error('Failed to check Misaka stock:', error)
    stockError.value = $t('dedicated_servers.browser.stock_error')
  }
}

function isPlanInStock(plan: MisakaPlan): boolean {
  return planStock.value?.[String(plan.id)] === 'in_stock'
}

function planStockLabel(plan: MisakaPlan): string {
  if (String(plan.id) === selectedPlanId.value) {
    return $t('dedicated_servers.browser.selected')
  }

  switch (planStock.value?.[String(plan.id)]) {
    case 'in_stock':
      return $t('dedicated_servers.browser.in_stock')
    case 'sold_out':
      return $t('dedicated_servers.browser.sold_out')
    default:
      return $t('dedicated_servers.browser.stock_unknown')
  }
}

function planBadgeVariant(plan: MisakaPlan) {
  if (String(plan.id) === selectedPlanId.value) {
    return 'default'
  }

  switch (planStock.value?.[String(plan.id)]) {
    case 'in_stock':
      return 'successOutline'
    case 'sold_out':
      return 'secondary'
    default:
      return 'outline'
  }
}

function selectRegion(region: MisakaRegion) {
  selectedRegionId.value = region.id
}

function selectRecommendedRegion() {
  if (!latencyRecommendation.value) {
    return
  }
  selectedRegionId.value = latencyRecommendation.value.regionId
}

function selectPlan(plan: MisakaPlan) {
  if (plansLoading.value) {
    return
  }
  if (!isPlanInStock(plan)) {
    return
  }
  selectedPlanId.value = String(plan.id)
}

function isHiddenRegion(region: MisakaRegion): boolean {
  return region.name === 'Frankfurt' || ['FRA02', 'FRA13'].includes(region.region_id || region.id)
}

function regionCode(region: MisakaRegion): string {
  return (region.slug || region.id).toUpperCase()
}

function displayRegionName(region: MisakaRegion): string {
  if (isChineseLocale()) {
    return chineseRegionDisplayName(region)
  }

  if (region.country_code === 'US' && region.subdivision_code) {
    return `${region.name}, ${region.subdivision_code}`
  }

  return region.name
}

function isChineseLocale(): boolean {
  return String(locale.value || '').toLowerCase().startsWith('zh')
}

function chineseRegionName(region: MisakaRegion): string {
  return ZH_REGION_NAMES[region.name] || region.name
}

function chineseRegionDisplayName(region: MisakaRegion): string {
  const name = chineseRegionName(region)
  if (region.country_code === 'US' && region.subdivision_code) {
    return `${name}，${ZH_SUBDIVISION_NAMES[region.subdivision_code] || region.subdivision_code}`
  }

  return name
}

function chineseCountryName(region: MisakaRegion): string {
  const countryCode = region.country_code?.trim().toUpperCase()
  if (!countryCode || !/^[A-Z]{2}$/.test(countryCode)) {
    return region.country
  }

  return ZH_COUNTRY_DISPLAY_NAMES.of(countryCode) || region.country
}

function matchesRegionSearch(region: MisakaRegion, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) {
    return true
  }

  const haystack = [
    region.name,
    chineseRegionName(region),
    chineseRegionDisplayName(region),
    region.slug,
    region.id,
    region.region_id,
    region.network_type,
    region.facility,
    region.country,
    chineseCountryName(region),
    region.country_code,
    region.subdivision_code
  ].filter(Boolean).join(' ').toLowerCase()

  return haystack.includes(normalizedQuery)
}

function networkTypeBadgeClass(networkType?: string): string {
  const normalized = (networkType || '').toLowerCase()
  const base = 'w-24 shrink-0 justify-center rounded-md border px-2 py-0.5 text-center text-[11px] font-semibold leading-none shadow-none'

  if (normalized.includes('plus')) {
    return `${base} border-primary/30 bg-primary/10 text-primary`
  }
  if (normalized.includes('pro')) {
    return `${base} border-sky-500/30 bg-sky-500/10 text-sky-500`
  }
  if (normalized.includes('premium')) {
    return `${base} border-border/70 bg-muted/60 text-muted-foreground`
  }

  return `${base} border-border/70 bg-muted/60 text-muted-foreground`
}

function networkTypeDescription(networkType?: string): string {
  const normalized = (networkType || '').toLowerCase()
  if (normalized.includes('plus')) {
    return $t('dedicated_servers.browser.network_type_premium_plus')
  }
  if (normalized.includes('pro')) {
    return $t('dedicated_servers.browser.network_type_premium_pro')
  }
  if (normalized.includes('premium')) {
    return $t('dedicated_servers.browser.network_type_premium')
  }

  return $t('dedicated_servers.browser.network_type_default')
}

function continentKey(region: MisakaRegion): string {
  const raw = (region.contient || region.continent || 'other').toLowerCase()
  if (raw.includes('north')) {
    return 'north_america'
  }
  if (raw.includes('south')) {
    return 'south_america'
  }
  if (raw.includes('america')) {
    return 'north_america'
  }
  if (raw.includes('europe')) {
    return 'europe'
  }
  if (raw.includes('asia')) {
    return 'asia'
  }
  if (raw.includes('australia') || raw.includes('oceania')) {
    return 'australia'
  }
  if (raw.includes('africa')) {
    return 'africa'
  }

  return raw.replace(/[^a-z0-9]+/g, '_') || 'other'
}

function continentSortOrder(key: string): number {
  const order = ['north_america', 'south_america', 'europe', 'australia', 'asia', 'africa', 'other']
  const index = order.indexOf(key)
  return index === -1 ? order.length : index
}

function sortRegions(left: MisakaRegion, right: MisakaRegion): number {
  const continentDelta = continentSortOrder(continentKey(left)) - continentSortOrder(continentKey(right))
  if (continentDelta !== 0) {
    return continentDelta
  }

  if (left.country_code !== right.country_code) {
    if (left.country_code === 'US') {
      return -1
    }
    if (right.country_code === 'US') {
      return 1
    }
    return (left.country || '').localeCompare(right.country || '')
  }

  const regionDelta = regionCode(left).localeCompare(regionCode(right))
  if (regionDelta !== 0) {
    return regionDelta
  }

  return (left.network_type || '').localeCompare(right.network_type || '')
}

function speedtestsForRegion(region: MisakaRegion): SpeedtestTarget[] {
  const seen = new Set<string>()
  const targets: SpeedtestTarget[] = []

  for (const item of region.speedtests || []) {
    const rawURL = typeof item === 'string' ? item : item.url
    if (!rawURL) {
      continue
    }

    const baseURL = normalizeSpeedtestUrl(rawURL)
    if (!baseURL || !isIpv4SpeedtestUrl(baseURL) || seen.has(baseURL)) {
      continue
    }

    seen.add(baseURL)
    targets.push({
      baseURL,
      label: typeof item === 'string' ? displaySpeedtestAddress(baseURL) : item.label || item.name || displaySpeedtestAddress(baseURL),
      displayAddress: displaySpeedtestAddress(baseURL)
    })
  }

  return targets
}

function isIpv4SpeedtestUrl(baseURL: string): boolean {
  try {
    const hostname = new URL(baseURL).hostname
    return /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)
  } catch {
    return false
  }
}

function normalizeSpeedtestUrl(rawURL: string): string | null {
  try {
    const parsed = new URL(rawURL)
    const suffixes = ['/api/tcp-rtt', '/api/tcp-info', '/healthz', '/demo']
    for (const suffix of suffixes) {
      if (parsed.pathname.endsWith(suffix)) {
        parsed.pathname = parsed.pathname.slice(0, -suffix.length) || '/'
      }
    }

    parsed.pathname = parsed.pathname.replace(/\/+$/, '')
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString().replace(/\/$/, '')
  } catch {
    console.warn('Invalid hosting speedtest URL', rawURL)
    return null
  }
}

function displaySpeedtestAddress(baseURL: string): string {
  const parsed = new URL(baseURL)
  const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '')
  return `${parsed.host}${path}`
}

function speedtestDemoIP(endpoint: SpeedtestTarget): string {
  return new URL(endpoint.baseURL).hostname
}

function speedtestNetworkType(endpoint: SpeedtestTarget): string {
  return endpoint.label.replace(/\s*\(IPv4\)\s*$/i, '').trim() || endpoint.label
}

function buildLatencyRecommendation(candidates: MisakaRegion[]): LatencyRecommendation | null {
  let recommendation: LatencyRecommendation | null = null

  for (const region of candidates) {
    for (const endpoint of speedtestsForRegion(region)) {
      const record = endpointLatency[`${region.id}:${endpoint.baseURL}`]
      if (!Number.isFinite(record?.bestMs)) {
        continue
      }

      if (recommendation && Number(record.bestMs) >= recommendation.bestMs) {
        continue
      }

      recommendation = {
        regionId: region.id,
        locationName: `${displayRegionName(region)} · ${regionCode(region)}`,
        networkType: speedtestNetworkType(endpoint),
        demoIP: speedtestDemoIP(endpoint),
        bestMs: Number(record.bestMs)
      }
    }
  }

  return recommendation
}

function formatTransfer(value: number): string {
  if (!Number.isFinite(value)) {
    return 'N/A'
  }

  if (value >= 1024) {
    return `${trimNumber(value / 1024)} TiB`
  }

  return `${trimNumber(value)} GiB`
}

function formatTrafficPlan(plan: MisakaPlan): string {
  return $t('dedicated_servers.browser.traffic_plan', { traffic: formatTransfer(plan.transfer) })
}

function formatTrafficBilling(plan: MisakaPlan): string {
  return $t(`dedicated_servers.browser.traffic_billing_${normalizedTrafficBillingModel(plan.traffic_billing_model)}`)
}

function normalizedTrafficBillingModel(model?: string): 'max' | 'sum' {
  return model === 'sum' || model === 'BothDirection' ? 'sum' : 'max'
}

function formatEuro(value: number): string {
  return `€${trimNumber(value)}`
}

function trimNumber(value: number): string {
  return Number(value.toFixed(2)).toLocaleString('en-US')
}

function formatMs(value?: number): string {
  if (!Number.isFinite(value)) {
    return 'N/A'
  }

  return `${Math.round(Number(value))} ms`
}

function latencyLabel(region: MisakaRegion): string {
  const record = regionLatency[region.id]
  if (!record) {
    return '--'
  }
  if (record.status === 'queued') {
    return $t('dedicated_servers.browser.queued')
  }
  if (record.status === 'checking') {
    return $t('dedicated_servers.browser.checking')
  }
  if (record.status === 'running') {
    return ''
  }
  if (record.status === 'failed') {
    return 'N/A'
  }

  return formatMs(record.bestMs)
}

function isLatencyPending(record?: LatencyRecord): boolean {
  return record?.status === 'queued' || record?.status === 'checking' || record?.status === 'running'
}

function latencyQualityLabel(record?: LatencyRecord, region?: MisakaRegion): string {
  if (!record?.bestMs) {
    return ''
  }

  const thresholds = latencyThresholdsForChina(region)
  if (record.bestMs <= thresholds.excellent) {
    return $t('dedicated_servers.browser.excellent')
  }
  if (record.bestMs <= thresholds.good) {
    return $t('dedicated_servers.browser.good')
  }
  if (record.bestMs <= thresholds.fair) {
    return $t('dedicated_servers.browser.fair')
  }

  return $t('dedicated_servers.browser.poor')
}

function latencyTextClass(record?: LatencyRecord, region?: MisakaRegion): string {
  if (!record?.bestMs) {
    return 'text-muted-foreground'
  }

  const thresholds = latencyThresholdsForChina(region)
  if (record.bestMs <= thresholds.excellent) {
    return 'text-emerald-600'
  }
  if (record.bestMs <= thresholds.good) {
    return 'text-sky-600'
  }
  if (record.bestMs <= thresholds.fair) {
    return 'text-amber-600'
  }

  return 'text-destructive'
}

function latencyThresholdsForChina(region?: MisakaRegion): { excellent: number, good: number, fair: number } {
  const country = (region?.country_code || '').toUpperCase()
  const continent = (region?.contient || region?.continent || '').toLowerCase()
  const subdivision = (region?.subdivision_code || '').toUpperCase()

  if (['CN', 'HK', 'MO', 'TW', 'JP', 'KR'].includes(country)) {
    return { excellent: 80, good: 130, fair: 200 }
  }

  if (['SG', 'MY', 'TH', 'VN', 'PH', 'ID'].includes(country)) {
    return { excellent: 110, good: 180, fair: 260 }
  }

  if (country === 'US') {
    if (['US-CA', 'US-WA', 'US-OR', 'US-NV', 'US-AZ'].includes(subdivision)) {
      return { excellent: 150, good: 220, fair: 300 }
    }
    return { excellent: 190, good: 260, fair: 340 }
  }

  if (country === 'CA') {
    return { excellent: 190, good: 260, fair: 340 }
  }

  if (['AU', 'NZ'].includes(country)) {
    return { excellent: 170, good: 240, fair: 320 }
  }

  if (continent === 'europe') {
    return { excellent: 220, good: 300, fair: 380 }
  }

  if (continent === 'south america' || continent === 'africa') {
    return { excellent: 280, good: 360, fair: 460 }
  }

  return { excellent: 180, good: 260, fair: 360 }
}

async function runLatencyTest() {
  if (latencyRunning.value) {
    return
  }

  const candidates = latencyCandidates.value
  if (!candidates.length) {
    return
  }

  latencyRecommendation.value = null
  latencyRunning.value = true
  latencySortActive.value = true

  for (const region of candidates) {
    regionLatency[region.id] = { status: 'queued', completed: 0, total: 10 }
  }

  try {
    await runPool(candidates, 8, runRegionLatency)
    latencyRecommendation.value = buildLatencyRecommendation(candidates)
    if (latencyRecommendation.value) {
      selectedRegionId.value = latencyRecommendation.value.regionId
    }
  } finally {
    latencyRunning.value = false
  }
}

async function runRegionLatency(region: MisakaRegion) {
  regionLatency[region.id] = { status: 'checking', completed: 0, total: 10 }

  const endpoints = speedtestsForRegion(region)
  await runPool(endpoints, 3, endpoint => runEndpointLatency(region, endpoint))

  const completed = endpoints
    .map(endpoint => endpointLatency[`${region.id}:${endpoint.baseURL}`])
    .filter(record => Number.isFinite(record?.bestMs))

  if (!completed.length) {
    regionLatency[region.id] = { status: 'failed', completed: 0, total: 10, message: 'No RTT samples collected' }
    return
  }

  completed.sort((left, right) => Number(left.bestMs) - Number(right.bestMs))
  const best = completed[0]
  regionLatency[region.id] = {
    status: 'done',
    bestMs: best.bestMs,
    meanMs: best.meanMs,
    maxMs: best.maxMs,
    lastMs: best.lastMs,
    completed: best.completed,
    total: best.total
  }
}

async function runEndpointLatency(region: MisakaRegion, endpoint: SpeedtestTarget) {
  const key = `${region.id}:${endpoint.baseURL}`
  endpointLatency[key] = { status: 'checking', completed: 0, total: 10 }
  const tcp = await probeTcpEndpoint(endpoint.baseURL)
  endpointLatency[key] = {
    ...endpointLatency[key],
    tcpRttMs: tcp.tcpRttMs
  }

  try {
    const stats = await measureWsEndpoint(endpoint.baseURL, (progress) => {
      endpointLatency[key] = {
        ...endpointLatency[key],
        status: 'running',
        bestMs: progress.minMs,
        meanMs: progress.meanMs,
        maxMs: progress.maxMs,
        lastMs: progress.lastMs,
        completed: progress.completed,
        total: progress.total
      }

      const currentBest = regionLatency[region.id]?.bestMs
      regionLatency[region.id] = {
        status: 'running',
        bestMs: Number.isFinite(currentBest) ? Math.min(Number(currentBest), progress.minMs) : progress.minMs,
        meanMs: progress.meanMs,
        maxMs: progress.maxMs,
        lastMs: progress.lastMs,
        completed: Math.max(regionLatency[region.id]?.completed || 0, progress.completed),
        total: progress.total
      }
    })

    endpointLatency[key] = stats.completed
      ? { ...endpointLatency[key], ...stats, status: 'done' }
      : { ...endpointLatency[key], status: 'failed', message: 'No WebSocket RTT samples' }
  } catch (error) {
    endpointLatency[key] = {
      ...endpointLatency[key],
      status: 'failed',
      message: error instanceof Error ? error.message : 'Latency test failed'
    }
  }
}

async function probeTcpEndpoint(baseURL: string): Promise<{ reachable: boolean, tcpRttMs?: number }> {
  const url = `${baseURL}/api/tcp-rtt?_t=${Date.now()}`
  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit'
    })
    if (!response.ok) {
      throw new Error(`TCP RTT returned ${response.status}`)
    }
    const data = await response.json()
    const tcpRttMs = Number(data.socket_rtt_ms)
    return {
      reachable: true,
      tcpRttMs: Number.isFinite(tcpRttMs) ? tcpRttMs : undefined
    }
  } catch {
    try {
      await fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        credentials: 'omit'
      })
      return { reachable: true }
    } catch {
      return { reachable: false }
    }
  }
}

async function measureWsEndpoint(baseURL: string, onProgress: (stats: LatencyStats) => void): Promise<LatencyStats> {
  const wsURL = buildWsUrl(baseURL)
  const samples: number[] = []
  const sentAt = new Map<number, number>()
  let sequence = 0

  return await new Promise((resolve, reject) => {
    const socket = new WebSocket(wsURL)
    socket.binaryType = 'arraybuffer'
    let finished = false
    const timeout = window.setTimeout(() => {
      finish(samples.length ? undefined : new Error('WebSocket RTT timed out'))
    }, 60000)

    function finish(error?: Error) {
      if (finished) {
        return
      }
      finished = true
      window.clearTimeout(timeout)
      try {
        socket.close()
      } catch {
        // Socket may already be closed by the browser.
      }

      if (error) {
        reject(error)
      } else {
        resolve(buildLatencyStats(samples, 10))
      }
    }

    function sendProbe() {
      if (finished) {
        return
      }
      if (samples.length >= 10) {
        finish()
        return
      }

      sequence += 1
      const payload = new ArrayBuffer(9)
      const view = new DataView(payload)
      view.setUint8(0, 3)
      writeUint64(view, 1, sequence)
      sentAt.set(sequence, performance.now())
      socket.send(payload)
    }

    socket.onopen = () => {
      socket.send(new Uint8Array([1, 112, 114, 111, 98, 101, 45, 114, 116, 116, 45, 118, 49]))
    }

    socket.onerror = () => {
      finish(new Error('WebSocket RTT failed'))
    }

    socket.onclose = () => {
      if (!finished) {
        finish(samples.length ? undefined : new Error('WebSocket RTT closed'))
      }
    }

    socket.onmessage = async (event) => {
      const buffer = await messageToArrayBuffer(event.data)
      const view = new DataView(buffer)
      const type = view.getUint8(0)

      if (type === 2) {
        window.setTimeout(sendProbe, 100)
        return
      }

      if (type !== 4 || buffer.byteLength < 9) {
        return
      }

      const id = readUint64(view, 1)
      const startedAt = sentAt.get(id)
      if (!startedAt) {
        return
      }

      sentAt.delete(id)
      samples.push(performance.now() - startedAt)
      const stats = buildLatencyStats(samples, 10)
      onProgress(stats)

      if (samples.length >= 10) {
        finish()
      } else {
        window.setTimeout(sendProbe, 100)
      }
    }
  })
}

function buildWsUrl(baseURL: string): string {
  const parsed = new URL(baseURL)
  parsed.protocol = parsed.protocol === 'http:' ? 'ws:' : 'wss:'
  parsed.pathname = `${parsed.pathname.replace(/\/$/, '')}/ws/rtt`
  parsed.search = ''
  parsed.hash = ''
  return parsed.toString()
}

async function messageToArrayBuffer(data: Blob | ArrayBuffer | string): Promise<ArrayBuffer> {
  if (data instanceof ArrayBuffer) {
    return data
  }
  if (data instanceof Blob) {
    return await data.arrayBuffer()
  }

  return new TextEncoder().encode(data).buffer
}

function writeUint64(view: DataView, offset: number, value: number) {
  const high = Math.floor(value / 0x100000000)
  const low = value >>> 0
  view.setUint32(offset, high, false)
  view.setUint32(offset + 4, low, false)
}

function readUint64(view: DataView, offset: number): number {
  return view.getUint32(offset, false) * 0x100000000 + view.getUint32(offset + 4, false)
}

function buildLatencyStats(samples: number[], total: number): LatencyStats {
  const minMs = Math.min(...samples)
  const maxMs = Math.max(...samples)
  const meanMs = samples.reduce((sum, sample) => sum + sample, 0) / samples.length

  return {
    minMs,
    meanMs,
    maxMs,
    lastMs: samples[samples.length - 1],
    completed: samples.length,
    total
  }
}

async function runPool<T>(items: T[], concurrency: number, worker: (item: T) => Promise<void>) {
  let index = 0
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (index < items.length) {
      const item = items[index]
      index += 1
      await worker(item)
    }
  })

  await Promise.all(workers)
}

async function contactStaticPlan(plan: string) {
  const planDetails: Record<string, string> = {
    'taiwan-professional': 'Taiwan residential broadband - 100 Mbps, 1 IP, unlimited traffic - €350/month',
    'taiwan-business': 'Taiwan residential broadband - 300 Mbps, 3 IPs, unlimited traffic - €550/month',
    'taiwan-enterprise': 'Taiwan residential broadband - 500 Mbps, 6 IPs, unlimited traffic - €750/month',
    'custom-global': 'Custom Global VPN line package'
  }
  const planParts = plan.split('-')
  const planName = planParts[1] ? planParts[1].charAt(0).toUpperCase() + planParts[1].slice(1) : plan

  await sendSalesInquiry($t('dedicated_servers.inquiry_message', {
    plan: planName,
    details: planDetails[plan] || plan
  }))
}

async function contactCustomPrivateLine() {
  const planName = $t('dedicated_servers.custom_private_line.title')
  const details = $t('dedicated_servers.custom_private_line.inquiry_details', {
    entry: selectedCustomLineEntryLabel.value,
    exit: selectedCustomLineExitLabel.value,
    bandwidth: customLineBandwidth.value,
    price: formatEuro(customLineMonthlyPrice.value)
  })

  await sendSalesInquiry($t('dedicated_servers.inquiry_message', { plan: planName, details }))
}

async function contactSales(custom = false) {
  if (!selectedRegion.value) {
    return
  }

  const region = selectedRegion.value
  const plan = custom ? undefined : selectedPlan.value
  const regionName = [displayRegionName(region), region.network_type].filter(Boolean).join(' ')
  const regionDetail = [regionCode(region), region.network_type].filter(Boolean).join(' / ')
  const planName = plan ? `${regionName} ${formatTrafficPlan(plan)}` : regionName
  const details = plan
    ? `${regionDetail} - ${formatTrafficPlan(plan)}, ${formatTrafficBilling(plan)}, ${formatEuro(plan.price_monthly || 0)}${$t('dedicated_servers.per_month')}`
    : `${regionDetail} - ${$t('dedicated_servers.custom_global.title')}`
  await sendSalesInquiry($t('dedicated_servers.inquiry_message', { plan: planName, details }))
}

async function sendSalesInquiry(message: string) {
  try {
    if (session.loggedIn) {
      const { conversation_id } = await user.createIntercomConversation({ content: message })

      if (conversation_id) {
        window.setTimeout(() => {
          useIntercom().showConversation(conversation_id)
        }, 1000)
      } else {
        useIntercom().showNewMessage?.(message)
      }
    } else {
      useIntercom().showNewMessage?.(message)
    }

    toast({
      title: $t('dedicated_servers.contact_initiated'),
      description: $t('dedicated_servers.contact_initiated_desc')
    })
  } catch (error) {
    console.error('Error opening chat:', error)
    toast({
      title: $t('error.str'),
      description: $t('error.contact_support'),
      variant: 'destructive'
    })
  }
}
</script>

<style scoped>

.hero-section,
.page-container {
  --ds-ease: cubic-bezier(0.16, 1, 0.3, 1);
  --ds-ink: var(--foreground);
  --ds-copy: var(--muted-foreground);
  --ds-line: var(--border);
  --ds-tint: color-mix(in oklch, var(--primary) 7%, var(--background));
  width: 100%;
  color: var(--ds-ink);
  font-weight: 400;
}

.hero-section {
  padding: clamp(3rem, 6vw, 5.5rem) 0 3.5rem;
}
.hero-inner,
.dedicated-main {
  width: var(--marketing-frame);
  margin-inline: auto;
}
.hero-inner {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  align-items: center;
  gap: 4rem;
}
.hero-copy {
  animation: dedicated-enter 700ms var(--ds-ease) both;
}
.hero-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin: 0 0 1.5rem;
  color: var(--ds-copy);
  font-family: ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0;
}
.eyebrow-line {
  width: 1.65rem;
  height: 2px;
  background: var(--primary);
}
.hero-title {
  margin: 0;
  color: var(--ds-ink);
  font-family: 'Metropolis', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-size: clamp(2.75rem, 5.6vw, 4.5rem);
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 1.05;
  text-wrap: balance;
}
.hero-subtitle {
  max-width: 34rem;
  margin-top: 1.5rem;
  color: var(--ds-copy);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.85;
  text-wrap: pretty;
}
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.75rem;
  margin-top: 2.25rem;
}
.hero-primary,
.hero-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 550;
  text-decoration: none;
}
.hero-primary {
  min-height: 2.875rem;
  padding: 0 1.4rem;
  border-radius: 999px;
  background: var(--primary);
  color: var(--primary-foreground);
  transition:
    transform 300ms var(--ds-ease),
    box-shadow 300ms var(--ds-ease);
}
.hero-secondary {
  min-height: 2.875rem;
  color: var(--ds-ink);
}
.hero-primary svg,
.hero-secondary svg {
  width: 1rem;
  height: 1rem;
  transition: transform 300ms var(--ds-ease);
}
.hero-secondary span {
  background: linear-gradient(var(--primary), var(--primary)) left bottom / 0 1px no-repeat;
  transition: background-size 350ms var(--ds-ease);
}
.hero-primary:active {
  transform: scale(0.97);
}
.hero-primary:focus-visible,
.hero-secondary:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 3px;
}
.hero-network {
  position: relative;
  height: 220px;
  animation: dedicated-enter 950ms 100ms var(--ds-ease) both;
}
.network-routes {
  width: 100%;
  height: 100%;
  overflow: visible;
}
.network-track {
  stroke: var(--ds-line);
  stroke-width: 1.5;
}
.network-signal {
  stroke: var(--primary);
  stroke-width: 1.5;
  stroke-dasharray: 18 460;
  animation: dedicated-signal 6s linear infinite;
}
.network-node {
  fill: var(--background);
  stroke: var(--primary);
  stroke-width: 1.5;
  transform-box: fill-box;
  transform-origin: center;
  animation: dedicated-node 6s var(--ds-ease) infinite;
}
.network-node:nth-of-type(2) { animation-delay: 1.4s; }
.network-node:nth-of-type(3) { animation-delay: 1.9s; }
.network-node:nth-of-type(4) { animation-delay: 2.4s; }
.network-server {
  position: absolute;
  top: 50%;
  left: 42%;
  display: grid;
  justify-items: center;
  gap: 0.75rem;
  width: 106px;
  padding: 1.25rem 1rem 1rem;
  border: 1px solid var(--ds-line);
  border-radius: 1rem;
  background: var(--card);
  transform: translate(-50%, -50%);
  box-shadow: 0 12px 32px -24px
    color-mix(in oklch, var(--foreground) 35%, transparent);
}
.network-server-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--primary);
}
.network-server > span {
  font-size: 0.5rem;
  font-weight: 650;
  letter-spacing: 0.12em;
}
.server-slots {
  display: grid;
  gap: 5px;
  width: 100%;
}
.server-slots i {
  height: 5px;
  border: 1px solid var(--ds-line);
  border-radius: 2px;
}
.server-slots i::before {
  content: "";
  display: block;
  width: 8px;
  height: 3px;
  border-radius: 1px;
  background: var(--primary);
  animation: dedicated-slot 6s var(--ds-ease) infinite;
}
.server-slots i:nth-child(2)::before { animation-delay: 0.5s; }
.server-slots i:nth-child(3)::before { animation-delay: 1s; }
.page-container {
  padding: 0 0 5rem;
}
.dedicated-catalog,
.page-container :deep(#dedicated-compare) {
  scroll-margin-top: 5rem;
}
.dedicated-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
  height: auto;
  margin: 0 0 2rem;
  padding: 0;
  gap: 0;
  border-bottom: 1px solid var(--ds-line);
  border-radius: 0;
  background: transparent;
  animation: dedicated-enter 700ms 80ms var(--ds-ease) both;
}
.dedicated-tab {
  position: relative;
  min-width: 0;
  min-height: 4.5rem;
  padding: 1rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--ds-copy);
  font-size: 0.95rem;
  font-weight: 500;
  white-space: normal;
  box-shadow: none;
  transition:
    color 220ms,
    background-color 220ms;
}
.dedicated-tab :deep(> span) {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.65rem;
  overflow: visible;
  white-space: normal;
}
.dedicated-tab-label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  line-height: 1.4;
}
.dedicated-tab-network-type {
  font-size: 0.75rem;
  font-weight: 400;
}
.dedicated-tab::after {
  content: "";
  position: absolute;
  inset: auto 0 -1px;
  height: 2px;
  background: var(--primary);
  transform: scaleX(0);
  transition: transform 350ms var(--ds-ease);
}
.dedicated-tab[data-state="active"] {
  color: var(--ds-ink);
  background: var(--ds-tint);
  box-shadow: none;
}
.dedicated-tab[data-state="active"]::after {
  transform: scaleX(1);
}
.dedicated-tab:hover {
  color: var(--ds-ink);
  background: var(--ds-tint);
}
.dedicated-tab-icon {
  width: 1.1rem;
  height: 1.1rem;
  color: var(--primary);
}
.page-container :deep([role="tabpanel"][data-state="active"]) {
  animation: dedicated-enter 450ms var(--ds-ease) both;
}
.custom-line-badge-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.25rem;
}
.global-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0 0 2rem;
  border-bottom: 1px solid var(--ds-line);
}
.global-stat {
  display: flex;
  flex-direction: column-reverse;
  gap: 0.6rem;
  min-width: 0;
  padding: 0.25rem 2rem 1.75rem 0;
}
.global-stat + .global-stat {
  padding-left: 2rem;
  border-left: 1px solid var(--ds-line);
}
.global-stat dt {
  color: var(--ds-copy);
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0;
}
.global-stat dd {
  display: flex;
  align-items: baseline;
  margin: 0;
  font-family: 'Metropolis', sans-serif;
  font-size: clamp(2.25rem, 4vw, 3.25rem);
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.global-stat-unit {
  margin-left: 0.06em;
  color: var(--ds-copy);
  font-size: 0.6em;
  letter-spacing: -0.01em;
}
.bandwidth-note {
  margin: 0 0 1.25rem;
  color: var(--ds-copy);
  font-size: 0.85rem;
  line-height: 1.8;
}
.catalog-footnote {
  display: flex;
  align-items: start;
  gap: 0.6rem;
  max-width: 52rem;
  margin: 1.25rem 0 0;
  font-size: 0.8rem;
}
.catalog-footnote svg {
  flex-shrink: 0;
  width: 0.95rem;
  height: 0.95rem;
  margin-top: 0.3rem;
}
.custom-line-shell > .bandwidth-note {
  margin: 0;
}
.unlimited-badge,
.custom-line-badge-row :deep([data-slot="badge"]) {
  gap: 0.3rem;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--ds-copy);
  font-size: 0.75rem;
  font-weight: 450;
  line-height: 1.6;
  box-shadow: none;
}
.unlimited-badge :deep(.iconify) {
  color: var(--primary);
}
.global-browser-grid {
  display: grid;
  grid-template-columns: minmax(17rem, 0.85fr) minmax(0, 1.75fr);
  align-items: start;
  gap: 1.5rem;
}
.catalog-card {
  min-width: 0;
  overflow: hidden;
  gap: 0;
  border: 1px solid var(--ds-line);
  border-radius: 1rem;
  background: var(--card);
  box-shadow: none;
}
.catalog-card :deep([data-slot="card-header"]) {
  padding: 1.5rem;
}
.catalog-card :deep([data-slot="card-title"]) {
  font-weight: 550;
}
.region-catalog :deep([data-slot="card-title"]) {
  font-size: 0.94rem;
}
.region-catalog :deep([data-slot="card-description"]) {
  font-size: 0.75rem;
}
.region-search {
  position: relative;
}
.region-search > svg {
  position: absolute;
  top: 50%;
  left: 0.8rem;
  width: 1rem;
  height: 1rem;
  color: var(--ds-copy);
  transform: translateY(-50%);
  pointer-events: none;
}
.region-search :deep(input) {
  padding-left: 2.2rem;
  background: var(--background);
  font-size: 0.8rem;
  box-shadow: none;
}
.latency-action {
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: none;
}
.misaka-region-list {
  max-height: 25rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: var(--ds-line) transparent;
  padding: 0.5rem;
  border-top: 1px solid var(--ds-line);
}
.misaka-region-list > button {
  min-height: 3.25rem;
  padding: 0.8rem 0.8rem 0.8rem 1rem;
  border-radius: 0.5rem;
}
.misaka-region-list > button::before {
  content: "";
  position: absolute;
  top: 0.7rem;
  bottom: 0.7rem;
  left: 0.35rem;
  width: 2px;
  border-radius: 2px;
  background: var(--primary);
  transform: scaleY(0);
  transition: transform 350ms var(--ds-ease);
}
.misaka-region-list > button[aria-pressed="true"]::before {
  transform: scaleY(1);
}
.misaka-region-list > button:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}
.misaka-region-list > button[aria-pressed="true"] {
  background: var(--ds-tint);
  box-shadow: inset 0 0 0 1px
    color-mix(in oklch, var(--primary) 25%, transparent);
}
.misaka-region-list :deep(.truncate) {
  font-size: 0.85rem;
  font-weight: 500;
}
.misaka-region-list :deep([data-slot="badge"]) {
  font-size: 0.68rem;
  font-weight: 500;
  padding: 0.12rem 0.35rem;
}
.recommended-location-card {
  box-shadow: none;
}
.plan-column {
  min-width: 0;
  position: sticky;
  top: 5rem;
}
.plan-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
}
.plan-heading > div:first-child {
  flex: 1 1 15rem;
  min-width: 0;
}
.region-title {
  font-size: clamp(1.2rem, 2vw, 1.55rem);
  line-height: 1.4;
  animation: dedicated-enter 350ms var(--ds-ease);
}
.plan-heading :deep(button) {
  min-height: 2.6rem;
  font-size: 0.78rem;
  font-weight: 550;
  box-shadow: none;
}
.plans-table-scroll :deep(table) {
  width: 100%;
  min-width: 0;
}
.plans-table-scroll :deep(th) {
  height: 3rem;
  padding: 0.75rem 1.25rem;
  color: var(--ds-copy);
  font-size: 0.72rem;
  font-weight: 450;
}
.plans-table-scroll :deep(td) {
  padding: 1.25rem;
}
.plans-table-scroll :deep(tr:last-child) {
  border-bottom: 0;
}
.plans-table-scroll :deep(td:first-child > div) {
  margin-top: 0.3rem;
  margin-left: 1.45rem;
  font-size: 0.75rem;
}
.plans-table-scroll :deep(tbody tr) {
  transition: background-color 250ms var(--ds-ease);
}
.plans-table-scroll :deep(tbody tr td:first-child) {
  box-shadow: inset 0 0 0 transparent;
  transition: box-shadow 300ms var(--ds-ease);
}
.plans-table-scroll :deep(tbody tr.bg-primary\/10 td:first-child) {
  box-shadow: inset 2px 0 0 var(--primary);
}
.plan-select {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.85rem;
  font-weight: 550;
  cursor: pointer;
}
.plan-select input {
  width: 0.9rem;
  height: 0.9rem;
  flex-shrink: 0;
  accent-color: var(--primary);
  cursor: pointer;
}
.plan-row-unavailable {
  cursor: not-allowed;
}
.plan-row-unavailable .plan-select,
.plan-row-unavailable .plan-select input {
  cursor: not-allowed;
}
.plan-row-unavailable .plan-select span,
.plan-row-unavailable .plan-price {
  color: var(--ds-copy);
}
.plan-row-unavailable:hover {
  background-color: transparent;
}
/* Keeps the price column still when the stock answer replaces the placeholders. */
.plan-status {
  display: flex;
  justify-content: flex-end;
  min-width: 5rem;
}
.plan-stock-checking {
  display: inline-block;
  width: 2.9rem;
  height: 1.35rem;
  border-radius: 0.5rem;
  background: var(--muted);
  vertical-align: middle;
  animation: plan-stock-pulse 1.4s var(--ds-ease) infinite;
}
@keyframes plan-stock-pulse {
  50% { opacity: 0.45; }
}
@media (prefers-reduced-motion: reduce) {
  .plan-stock-checking {
    animation: none;
  }
}
.plan-select input:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 4px;
}
.plan-price {
  font-family: 'Metropolis', sans-serif;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.plans-table-scroll :deep(td [data-slot="badge"]) {
  padding: 0.15rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 500;
}
.option-header {
  display: grid;
  justify-items: start;
  gap: 1rem;
  margin: 0 0 2rem;
}
.option-header p {
  margin: 0;
}
.taiwan-description,
.custom-line-description {
  color: var(--ds-ink);
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.75;
}
.taiwan-selling-point {
  display: flex;
  align-items: start;
  gap: 0.6rem;
  max-width: 46rem;
  color: var(--ds-copy);
  font-size: 0.85rem;
  line-height: 1.8;
}
.taiwan-selling-point > .iconify {
  flex-shrink: 0;
  margin-top: 0.3rem;
  color: var(--primary);
}
.pricing-grid {
  gap: 1rem;
  align-items: stretch;
}
.pricing-card {
  position: relative;
  min-width: 0;
  border: 1px solid var(--ds-line);
  border-radius: 1rem;
  background: var(--card);
  box-shadow: none;
  transition: border-color 150ms var(--ds-ease);
}
.pricing-card:hover {
  border-color: color-mix(in oklch, var(--primary) 45%, var(--ds-line));
}
.pricing-card.is-popular {
  border-color: color-mix(in oklch, var(--primary) 60%, var(--ds-line));
  background: var(--ds-tint);
}
.pricing-card-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 21rem;
  padding: 2rem 1.5rem 1.5rem;
}
.pricing-plan-name {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 550;
}
.pricing-price-row {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  margin-top: 1.75rem;
}
.pricing-price {
  font-family: 'Metropolis', sans-serif;
  font-size: clamp(2.25rem, 3vw, 2.75rem);
  font-weight: 500;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.pricing-period {
  color: var(--ds-copy);
  font-size: 0.75rem;
}
.setup-fee-line {
  min-height: 1.25rem;
  margin-top: 0.5rem;
  color: var(--ds-copy);
  font-size: 0.75rem;
}
.pricing-specs {
  display: grid;
  gap: 0.85rem;
  margin-block: 1.5rem 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--ds-line);
  font-weight: 400;
}
.popular-badge {
  position: absolute;
  top: -0.7rem;
  right: 1.25rem;
  padding: 0.2rem 0.65rem;
  border: 0;
  border-radius: 0.4rem;
  background: var(--primary);
  color: var(--primary-foreground);
  font-size: 0.65rem;
  font-weight: 550;
}
.pricing-action {
  min-height: 2.65rem;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
  box-shadow: none;
}
.custom-line-shell {
  display: grid;
  gap: 2.5rem;
}
.custom-line-hero {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  align-items: center;
  gap: 3rem;
}
.custom-line-description {
  margin-top: 1rem;
}
.custom-line-highlights {
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;
}
.custom-line-highlight {
  display: flex;
  align-items: start;
  gap: 0.8rem;
}
.custom-line-highlight > .iconify {
  flex-shrink: 0;
  width: 1.1rem;
  height: 1.1rem;
  margin-top: 0.15rem;
}
.custom-line-highlight strong,
.custom-line-highlight span {
  display: block;
}
.custom-line-highlight strong {
  font-size: 0.85rem;
  font-weight: 550;
}
.custom-line-highlight span {
  margin-top: 0.25rem;
  color: var(--ds-copy);
  font-size: 0.8rem;
  line-height: 1.7;
}
.custom-line-network-panel {
  min-width: 0;
}
.private-route {
  position: relative;
  display: grid;
  gap: 1.4rem;
  max-width: 17rem;
  margin-inline: auto;
  padding: 0 0 0 2rem;
  list-style: none;
}
.private-route::before {
  content: '';
  position: absolute;
  top: 0.6rem;
  bottom: 0.6rem;
  left: 0.3rem;
  width: 1px;
  background: color-mix(in oklch, var(--primary) 35%, var(--ds-line));
}
.private-route li {
  position: relative;
}
.private-route li::before {
  content: '';
  position: absolute;
  top: 0.45rem;
  left: -2rem;
  width: 0.65rem;
  height: 0.65rem;
  border: 1px solid var(--primary);
  border-radius: 50%;
  background: var(--background);
}
.private-route-terminal {
  color: var(--ds-copy);
  font-size: 0.75rem;
}
.private-route-stop > span {
  display: block;
  color: var(--ds-copy);
  font-size: 0.7rem;
}
.route-value {
  display: block;
  margin-top: 0.25rem;
  color: var(--ds-ink);
  font-size: 1.1rem;
  font-weight: 550;
  animation: dedicated-enter 300ms var(--ds-ease);
}
.custom-line-config-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 18rem;
  gap: 3rem;
  align-items: start;
  padding-top: 2.5rem;
  border-top: 1px solid var(--ds-line);
}
.custom-line-controls {
  display: grid;
  gap: 2rem;
}
.custom-line-field {
  display: grid;
  gap: 0.9rem;
}
.custom-line-field p,
.custom-line-field :deep(label) {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 500;
}
.custom-line-option-row {
  display: grid;
  gap: 0.75rem;
}
.custom-line-option-row--entry {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.custom-line-option-row--exit {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.custom-line-choice {
  min-height: 3.5rem;
  border-radius: 0.6rem;
  font-size: 0.9rem;
  font-weight: 450;
  box-shadow: none;
  transition:
    background-color 220ms,
    border-color 220ms,
    transform 220ms var(--ds-ease);
}
.custom-line-choice.is-selected {
  border: 1px solid var(--primary);
  background: var(--ds-tint);
  color: var(--ds-ink);
}
.custom-line-choice:active {
  transform: scale(0.98);
}
.custom-line-bandwidth-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.custom-line-bandwidth-value {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  color: var(--ds-copy);
  font-size: 0.8rem;
}
.bandwidth-input {
  width: 5ch;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0;
  appearance: textfield;
  color: var(--ds-ink);
  font-size: 2rem;
  font-weight: 550;
  line-height: 1.2;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.bandwidth-input::-webkit-outer-spin-button,
.bandwidth-input::-webkit-inner-spin-button {
  margin: 0;
  appearance: none;
}
.bandwidth-input:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 0.25rem;
  border-radius: 0.2rem;
}
.custom-line-quote {
  position: sticky;
  top: 5rem;
  padding: 1.75rem;
  border: 1px solid var(--ds-line);
  border-radius: 1rem;
  background: var(--ds-tint);
}
.custom-line-quote-label {
  color: var(--ds-copy);
  font-size: 0.8rem;
}
.custom-line-quote-price {
  margin-top: 1.2rem;
  font-family: 'Metropolis', sans-serif;
  font-size: 2.75rem;
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.custom-line-quote-copy {
  margin-top: 1rem;
  color: var(--ds-copy);
  font-size: 0.8rem;
  line-height: 1.7;
}
.custom-line-cta {
  width: 100%;
  min-height: 2.75rem;
  margin-top: 1.75rem;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
  box-shadow: none;
}
.dedicated-info-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 4rem;
  border-block: 1px solid var(--ds-line);
}
.dedicated-info-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 2rem 2.5rem 2.25rem 0;
}
.dedicated-info-item + .dedicated-info-item {
  padding-left: 2.5rem;
  border-left: 1px solid var(--ds-line);
}
.info-index {
  margin-bottom: 1.5rem;
  color: var(--ds-copy);
  font-family: ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
}
.dedicated-info-item h3,
.feature-item h3 {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.5;
}
.dedicated-info-item p,
.feature-item p {
  margin-top: 0.6rem;
  color: var(--ds-copy);
  font-size: 0.85rem;
  line-height: 1.8;
}
.info-action {
  display: inline-flex;
  align-items: center;
  align-self: start;
  gap: 0.4rem;
  margin-top: 1.25rem;
  color: var(--ds-ink);
  font-size: 0.85rem;
  font-weight: 550;
}
.info-action span {
  background: linear-gradient(var(--primary), var(--primary)) left bottom / 100% 1px no-repeat;
  padding-bottom: 2px;
}
.info-action svg {
  width: 0.95rem;
  height: 0.95rem;
  color: var(--primary);
  transition: transform 300ms var(--ds-ease);
}
.info-action:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 4px;
  border-radius: 2px;
}
.dedicated-section {
  margin-top: 6rem;
}
.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  color: var(--ds-copy);
  font-family: ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
  letter-spacing: 0;
}
.eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary);
}
.section-heading h2 {
  color: var(--ds-ink);
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.3;
  text-wrap: balance;
}
.feature-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 3rem;
  border-top: 1px solid var(--ds-line);
}
.feature-item {
  min-width: 0;
  padding: 2rem 2rem 0 0;
}
.feature-item + .feature-item {
  padding-left: 2rem;
  border-left: 1px solid var(--ds-line);
}
.feature-icon {
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 1.5rem;
  color: var(--primary);
  transition: transform 400ms var(--ds-ease);
}
.dedicated-faq-section {
  display: grid;
  grid-template-columns: 1fr 1.6fr;
  column-gap: 4rem;
  align-items: start;
  padding-top: 4rem;
  border-top: 1px solid var(--ds-line);
}
.dedicated-faq-list {
  min-width: 0;
}
.dedicated-faq-list :deep(.dedicated-faq-item) {
  border-color: var(--ds-line);
}
.dedicated-faq-list :deep(.dedicated-faq-trigger) {
  gap: 1.5rem;
  padding-block: 1.5rem;
  color: var(--ds-ink);
  font-size: 0.95rem;
  font-weight: 450;
  line-height: 1.7;
  text-align: left;
  text-decoration: none;
  transition: color 250ms var(--ds-ease);
}
.dedicated-faq-list :deep(.faq-plus) {
  flex: none;
  color: var(--ds-copy);
  font-size: 1.5rem;
  font-weight: 300;
  line-height: 1;
  transition: transform 300ms var(--ds-ease), color 250ms;
}
.dedicated-faq-list :deep(.dedicated-faq-trigger[data-state='open'] .faq-plus) {
  color: var(--primary);
  transform: rotate(45deg);
}
.dedicated-faq-list :deep(.dedicated-faq-answer) {
  padding-right: 2.25rem;
  color: var(--ds-copy);
  font-size: 0.875rem;
  line-height: 1.9;
}
.reveal {
  transition:
    opacity 700ms var(--ds-ease),
    transform 700ms var(--ds-ease);
}
.reveal-pending {
  opacity: 0;
  transform: translateY(12px);
}
.reveal-pending:focus-within {
  opacity: 1;
  transform: none;
}
@media (hover: hover) and (pointer: fine) {
  .hero-primary:hover svg {
    transform: translateY(2px);
  }
  .hero-secondary:hover span {
    background-size: 100% 1px;
  }
  .hero-secondary:hover svg,
  .info-action:hover svg {
    transform: translateX(3px);
  }
  .dedicated-faq-list :deep(.dedicated-faq-trigger:hover) {
    color: var(--primary);
  }
}

@keyframes dedicated-enter {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes dedicated-signal {
  to {
    stroke-dashoffset: -478;
  }
}
@keyframes dedicated-node {
  0%, 18%, 100% {
    transform: scale(1);
  }
  9% {
    transform: scale(1.35);
  }
}
@keyframes dedicated-slot {
  0%, 100% {
    width: 8px;
  }
  40%, 60% {
    width: 70%;
  }
}

@media (max-width: 1100px) {
  .hero-inner {
    gap: 2rem;
  }
  .global-browser-grid {
    grid-template-columns: 18rem minmax(0, 1fr);
  }
  .catalog-card :deep([data-slot="card-header"]) {
    padding: 1.2rem;
  }
  .plans-table-scroll :deep(td),
  .plans-table-scroll :deep(th) {
    padding-inline: 0.85rem;
  }
  .custom-line-config-grid {
    gap: 2rem;
  }
  .pricing-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 1.75rem;
  }
  .feature-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .feature-item:nth-child(3) {
    padding-left: 0;
    border-left: 0;
  }
  .feature-item:nth-child(n + 3) {
    border-top: 1px solid var(--ds-line);
    margin-top: 2rem;
  }
  .dedicated-info-item {
    padding-right: 1.5rem;
  }
  .dedicated-info-item + .dedicated-info-item {
    padding-left: 1.5rem;
  }
}
@media (max-width: 767px) {
  .hero-section {
    padding: 2.75rem 0 2rem;
  }
  .hero-inner {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .hero-network {
    display: none;
  }
  .hero-eyebrow {
    margin-bottom: 1rem;
    font-size: 0.7rem;
  }
  .hero-title {
    font-size: clamp(2.35rem, 7vw, 3rem);
  }
  .hero-subtitle {
    max-width: 36rem;
    margin-top: 1rem;
    font-size: 0.9rem;
    line-height: 1.8;
  }
  .page-container {
    padding: 0 0 3rem;
  }
  .dedicated-tabs {
    margin-bottom: 1.5rem;
  }
  .dedicated-tab {
    min-height: 4.5rem;
    padding: 0.7rem 0.35rem;
    font-size: 0.78rem;
    white-space: normal;
  }
  .dedicated-tab :deep(> span) {
    flex-direction: column;
    gap: 0.45rem;
    width: 100%;
    white-space: normal;
  }
  .dedicated-tab-icon {
    width: 1rem;
    height: 1rem;
  }
  .hero-actions {
    margin-top: 1.75rem;
  }
  .global-stats {
    margin-bottom: 1.5rem;
  }
  .global-stat {
    padding: 0 0.75rem 1.25rem 0;
  }
  .global-stat + .global-stat {
    padding-left: 0.75rem;
  }
  .global-stat dt {
    font-size: 0.75rem;
  }
  .global-stat dd {
    font-size: clamp(1.6rem, 8vw, 2.25rem);
  }
  .global-browser-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
  .misaka-region-list {
    max-height: 14rem;
  }
  .plan-column {
    position: static;
  }
  .catalog-card :deep([data-slot="card-header"]) {
    padding: 1.15rem;
  }
  .region-search :deep(input) {
    font-size: 16px;
  }
  .latency-action {
    min-height: 2.75rem;
  }
  .plan-heading {
    gap: 1rem;
  }
  .plan-heading > div {
    width: 100%;
  }
  .plan-heading :deep(button) {
    width: 100%;
    min-height: 2.75rem;
  }
  .plans-table-scroll :deep(th),
  .plans-table-scroll :deep(td) {
    padding: 1rem 0.65rem;
  }
  .plan-price {
    font-size: 0.88rem;
  }
  .plan-select {
    font-size: 0.78rem;
  }
  .plans-table-scroll :deep(td:first-child > div) {
    margin-left: 0;
    font-size: 0.7rem;
  }
  .plans-table-scroll :deep(td [data-slot="badge"]) {
    padding-inline: 0.35rem;
    font-size: 0.68rem;
  }
  .pricing-grid {
    gap: 1.5rem 1rem;
  }
  .pricing-card-content {
    min-height: 20rem;
    padding: 1.5rem 1.15rem 1.15rem;
  }
  .pricing-price {
    font-size: 2.4rem;
  }
  .custom-line-hero {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  .custom-line-network-panel {
    max-width: 30rem;
    width: 100%;
    margin-inline: auto;
  }
  .custom-line-config-grid {
    grid-template-columns: 1fr;
    padding-top: 1.75rem;
  }
  .custom-line-quote {
    position: static;
    padding: 1.5rem;
  }
  .custom-line-choice {
    font-size: 0.8rem;
  }
  .custom-line-option-row {
    gap: 0.5rem;
  }
  .custom-line-bandwidth-header {
    flex-wrap: wrap;
  }
  .custom-line-bandwidth-value {
    margin-left: auto;
  }
  .dedicated-info-strip {
    grid-template-columns: 1fr;
    margin-top: 2.5rem;
  }
  .dedicated-info-item,
  .dedicated-info-item + .dedicated-info-item {
    padding: 1.5rem 0;
    border-left: 0;
  }
  .dedicated-info-item + .dedicated-info-item {
    border-top: 1px solid var(--ds-line);
  }
  .info-index {
    margin-bottom: 0.9rem;
  }
  .dedicated-section {
    margin-top: 4rem;
  }
  .feature-grid {
    grid-template-columns: 1fr;
    margin-top: 2rem;
  }
  .feature-item,
  .feature-item + .feature-item,
  .feature-item:nth-child(n + 3) {
    display: grid;
    grid-template-columns: 1.25rem minmax(0, 1fr);
    column-gap: 1rem;
    margin-top: 0;
    padding: 1.5rem 0;
    border-left: 0;
  }
  .feature-item + .feature-item {
    border-top: 1px solid var(--ds-line);
  }
  .feature-icon {
    grid-row: span 2;
    margin: 0.15rem 0 0;
  }
  .feature-item p {
    grid-column: 2;
  }
  .dedicated-faq-section {
    grid-template-columns: 1fr;
    row-gap: 1.5rem;
    padding-top: 3rem;
  }
  .dedicated-faq-list :deep(.dedicated-faq-trigger) {
    padding-block: 1.25rem;
    font-size: 0.9rem;
  }
}
@media (max-width: 420px) {
  .pricing-grid {
    grid-template-columns: 1fr;
  }
  .pricing-card-content {
    min-height: 0;
    padding: 1.5rem;
  }
  .custom-line-option-row--exit {
    grid-template-columns: 1fr;
  }
  .plans-table-scroll :deep(td),
  .plans-table-scroll :deep(th) {
    padding-inline: 0.5rem;
  }
  .plan-price {
    font-size: 0.8rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .hero-section *,
  .hero-section *::before,
  .page-container *,
  .page-container *::before,
  .page-container *::after,
  .page-container :deep(*) {
    animation: none !important;
    transition: none !important;
  }
}
</style>
